<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  FIELD_TYPES,
  deepClone,
  lintDefinition,
  packageFileName,
  packageWithClinicalMask,
  packageWithIntegrity,
  parseClinicCrfDocument,
  prettyJson,
  safeFilePart,
  validateDefinitionV2,
  validatePackage,
  verifyPackageIntegrity,
  type ClinicCrfCondition,
  type ClinicCrfField,
  type ClinicCrfFieldPresentation,
  type ClinicCrfFieldType,
  type ClinicCrfPackageV1,
  type ClinicCrfMask,
  type ClinicCrfRule,
  type ClinicCrfTemplate,
} from '@clinic-crf/contract'
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp, BadgeCheck, Braces,
  Check, ChevronDown, CircleHelp, ClipboardList, Copy, Download, ExternalLink,
  FileCheck2, FileCog, FileJson, FilePlus2, FlaskConical, GripVertical, LayoutList,
  LibraryBig, PanelLeftClose, Plus, Printer, Redo2, Save, Settings2, ShieldCheck, Sparkles,
  SquareCheckBig, Trash2, Undo2,
} from 'lucide-vue-next'
import ConditionEditor from '@/components/ConditionEditor.vue'
import FieldEditor from '@/components/FieldEditor.vue'
import MaskLibrary from '@/components/MaskLibrary.vue'
import PreviewPane from '@/components/PreviewPane.vue'
import QualityPanel from '@/components/QualityPanel.vue'
import WelcomeScreen from '@/components/WelcomeScreen.vue'
import {
  MAKER_DRAFT_KEY,
  MAX_IMPORT_BYTES,
  SnapshotHistory,
  createField,
  duplicateField,
  moveItem,
  readDraft,
  removeField,
  renameFieldPath,
  uniqueSectionId,
  writeDraft,
} from '@/lib/editor'

type WorkspaceMode = 'build' | 'preview' | 'quality'
type Selection = 'form' | 'rules' | `section:${string}` | `field:${string}`

const view = ref<'welcome' | 'editor'>('welcome')
const current = ref<ClinicCrfPackageV1 | null>(null)
const selection = ref<Selection>('form')
const mode = ref<WorkspaceMode>('build')
const sidebarOpen = ref(true)
const annotated = ref(false)
const maskLibraryOpen = ref(false)
const draft = ref<{ package: ClinicCrfPackageV1; savedAt: string } | null>(null)
const importError = ref('')
const saveState = ref<'idle' | 'saving' | 'saved' | 'error'>('idle')
const toast = ref('')
const canUndo = ref(false)
const canRedo = ref(false)
let history: SnapshotHistory<ClinicCrfPackageV1> | null = null
let historyTimer: number | undefined
let saveTimer: number | undefined
let toastTimer: number | undefined
let suppressWatch = false

const packageValue = computed(() => current.value as ClinicCrfPackageV1)
const selectedField = computed(() => selection.value.startsWith('field:')
  ? packageValue.value.definition.fields.find((field) => field.path === selection.value.slice(6)) ?? null
  : null)
const selectedSection = computed(() => selection.value.startsWith('section:')
  ? packageValue.value.presentation?.sections.find((section) => section.id === selection.value.slice(8)) ?? null
  : null)
const selectedPresentation = computed<ClinicCrfFieldPresentation>(() => {
  if (!selectedField.value) return {}
  return packageValue.value.presentation?.fields?.[selectedField.value.path] ?? {}
})
const qualityIssues = computed(() => current.value ? [...validatePackage(current.value).issues, ...lintDefinition(current.value.definition)] : [])
const qualityErrors = computed(() => qualityIssues.value.filter((issue) => issue.severity === 'error').length)
const draftLabel = computed(() => draft.value
  ? `${draft.value.package.form.name} · zapis ${new Intl.DateTimeFormat('pl', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(draft.value.savedAt))}`
  : undefined)

const fieldTypeLabels: Record<string, string> = {
  string: 'Krótki tekst', text: 'Długi tekst', integer: 'Liczba całkowita', number: 'Liczba dziesiętna',
  boolean: 'Tak / nie', date: 'Data', datetime: 'Data i czas', choice: 'Jednokrotny wybór',
  multichoice: 'Wielokrotny wybór', partial_date: 'Data częściowa',
}

watch(current, () => {
  if (!current.value || suppressWatch) return
  saveState.value = 'saving'
  window.clearTimeout(historyTimer)
  window.clearTimeout(saveTimer)
  historyTimer = window.setTimeout(() => {
    if (!current.value || suppressWatch) return
    history?.push(current.value)
    syncHistoryState()
  }, 450)
  saveTimer = window.setTimeout(() => {
    if (!current.value) return
    try {
      const savedAt = writeDraft(localStorage, current.value)
      draft.value = { package: deepClone(current.value), savedAt }
      saveState.value = 'saved'
    } catch {
      saveState.value = 'error'
    }
  }, 750)
}, { deep: true })

onMounted(() => {
  try { draft.value = readDraft(localStorage) } catch { draft.value = null }
  window.addEventListener('keydown', keyboardShortcuts)
})

onBeforeUnmount(() => {
  window.clearTimeout(historyTimer)
  window.clearTimeout(saveTimer)
  window.clearTimeout(toastTimer)
  window.removeEventListener('keydown', keyboardShortcuts)
})

function start(packageInput: ClinicCrfPackageV1): void {
  current.value = deepClone(packageInput)
  current.value.provenance.generator = 'clinic-ecrf-maker'
  current.value.provenance.generator_version = '0.1.0'
  delete current.value.integrity
  history = new SnapshotHistory(current.value)
  selection.value = 'form'
  mode.value = 'build'
  sidebarOpen.value = true
  importError.value = ''
  view.value = 'editor'
  syncHistoryState()
  try {
    const savedAt = writeDraft(localStorage, current.value)
    draft.value = { package: deepClone(current.value), savedAt }
    saveState.value = 'saved'
  } catch { saveState.value = 'error' }
}

function chooseTemplate(template: ClinicCrfTemplate): void { start(template.package) }
function resumeDraft(): void { if (draft.value) start(draft.value.package) }
function goHome(): void { view.value = 'welcome'; importError.value = '' }

async function importFile(file: File): Promise<void> {
  importError.value = ''
  if (file.size > MAX_IMPORT_BYTES) {
    importError.value = 'Plik jest większy niż 2 MB. Wybierz mniejszy pakiet JSON.'
    return
  }
  if (!file.name.toLowerCase().endsWith('.json')) {
    importError.value = 'Obsługiwane są wyłącznie pliki JSON.'
    return
  }
  try {
    const result = parseClinicCrfDocument(await file.text())
    if (!result.value) {
      importError.value = result.issues.slice(0, 3).map((issue) => issue.message).join(' ')
      return
    }
    const integrity = await verifyPackageIntegrity(result.value)
    if (integrity === false) {
      importError.value = 'Suma SHA-256 nie zgadza się z definicją. Import przerwany, aby nie ukryć zmiany pliku.'
      return
    }
    start(result.value)
    if (result.issues.length) notify(`Zaimportowano z ${result.issues.length} uwagami migracji.`)
    else notify('Pakiet został bezpiecznie zaimportowany.')
  } catch (error) {
    importError.value = `Nie udało się odczytać pliku: ${(error as Error).message}`
  }
}

function syncHistoryState(): void {
  canUndo.value = history?.canUndo ?? false
  canRedo.value = history?.canRedo ?? false
}

function selectItem(target: Selection): void {
  selection.value = target
  if (window.matchMedia('(max-width: 800px)').matches) sidebarOpen.value = false
}

async function restore(snapshot: ClinicCrfPackageV1 | null): Promise<void> {
  if (!snapshot) return
  suppressWatch = true
  current.value = snapshot
  if (selection.value.startsWith('field:') && !selectedField.value) selection.value = 'form'
  await nextTick()
  suppressWatch = false
  syncHistoryState()
  saveState.value = 'saving'
  try {
    const savedAt = writeDraft(localStorage, snapshot)
    draft.value = { package: deepClone(snapshot), savedAt }
    saveState.value = 'saved'
  } catch { saveState.value = 'error' }
}

function undo(): void { void restore(history?.undo() ?? null) }
function redo(): void { void restore(history?.redo() ?? null) }

function keyboardShortcuts(event: KeyboardEvent): void {
  if (event.key === 'Escape' && maskLibraryOpen.value) {
    event.preventDefault()
    maskLibraryOpen.value = false
    return
  }
  if (view.value !== 'editor' || !(event.metaKey || event.ctrlKey)) return
  if (event.key.toLowerCase() === 'z' && !event.shiftKey) { event.preventDefault(); undo() }
  else if ((event.key.toLowerCase() === 'z' && event.shiftKey) || event.key.toLowerCase() === 'y') { event.preventDefault(); redo() }
  else if (event.key.toLowerCase() === 's') { event.preventDefault(); void exportPackage() }
}

function notify(message: string): void {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => { toast.value = '' }, 3200)
}

function updateForm(key: 'name' | 'code' | 'version' | 'description', value: string | number | undefined): void {
  const form = packageValue.value.form
  if (key === 'description' && !value) delete form.description
  else Object.assign(form, { [key]: value })
}

function updateLocale(value: string): void {
  packageValue.value.presentation ??= { default_locale: 'pl', sections: [], fields: {} }
  packageValue.value.presentation.default_locale = value
}

function addSection(): void {
  const sections = packageValue.value.presentation?.sections ?? []
  const id = uniqueSectionId(`section_${sections.length + 1}`, sections.map((section) => section.id))
  packageValue.value.presentation ??= { default_locale: 'pl', sections: [], fields: {} }
  packageValue.value.presentation.sections.push({ id, title: 'Nowa sekcja', field_paths: [] })
  selectItem(`section:${id}`)
}

function moveSection(index: number, direction: -1 | 1): void {
  if (!packageValue.value.presentation) return
  packageValue.value.presentation.sections = moveItem(packageValue.value.presentation.sections, index, index + direction)
}

function deleteSection(id: string): void {
  const sections = packageValue.value.presentation?.sections
  if (!sections || sections.length <= 1) return notify('Formularz musi mieć co najmniej jedną sekcję.')
  const index = sections.findIndex((section) => section.id === id)
  const section = sections[index]
  if (!section) return
  if (section.field_paths.length && !window.confirm(`Przenieść ${section.field_paths.length} pól do pierwszej pozostałej sekcji i usunąć „${section.title}”?`)) return
  const target = sections.find((candidate) => candidate.id !== id)
  if (target) target.field_paths.push(...section.field_paths)
  sections.splice(index, 1)
  selection.value = target ? `section:${target.id}` : 'form'
  syncDefinitionOrder()
}

function updateSection(key: 'id' | 'title' | 'description', value: string): void {
  const section = selectedSection.value
  if (!section) return
  if (key === 'id') {
    if (!value) return
    section.id = value
    selection.value = `section:${value}`
  } else if (key === 'description' && !value) delete section.description
  else section[key] = value
}

function addField(sectionId: string, type: ClinicCrfFieldType): void {
  const field = createField(type, packageValue.value.definition.fields)
  packageValue.value.definition.fields.push(field)
  const section = packageValue.value.presentation?.sections.find((candidate) => candidate.id === sectionId)
  section?.field_paths.push(field.path)
  packageValue.value.presentation ??= { default_locale: 'pl', sections: [] }
  packageValue.value.presentation.fields ??= {}
  packageValue.value.presentation.fields[field.path] = { width: 'full' }
  selectItem(`field:${field.path}`)
  syncDefinitionOrder()
}

function updateField(value: ClinicCrfField): void {
  if (!selectedField.value) return
  const index = packageValue.value.definition.fields.findIndex((field) => field.path === selectedField.value?.path)
  if (index >= 0) packageValue.value.definition.fields[index] = value
}

function updateFieldPresentation(value: ClinicCrfFieldPresentation): void {
  if (!selectedField.value) return
  packageValue.value.presentation ??= { default_locale: 'pl', sections: [] }
  packageValue.value.presentation.fields ??= {}
  packageValue.value.presentation.fields[selectedField.value.path] = value
}

function renameField(oldPath: string, newPath: string): void {
  if (!newPath || oldPath === newPath) return
  renameFieldPath(packageValue.value, oldPath, newPath)
  selection.value = `field:${newPath}`
  syncDefinitionOrder()
}

function moveField(sectionId: string, fieldIndex: number, direction: -1 | 1): void {
  const section = packageValue.value.presentation?.sections.find((candidate) => candidate.id === sectionId)
  if (!section) return
  section.field_paths = moveItem(section.field_paths, fieldIndex, fieldIndex + direction)
  syncDefinitionOrder()
}

function copyField(path: string): void {
  const created = duplicateField(packageValue.value, path)
  if (created) { selection.value = `field:${created}`; syncDefinitionOrder(); notify('Pole zostało zduplikowane.') }
}

function deleteField(path: string): void {
  if (packageValue.value.definition.fields.length <= 1) return notify('Kontrakt wymaga co najmniej jednego pola.')
  const field = packageValue.value.definition.fields.find((candidate) => candidate.path === path)
  if (!field || !window.confirm(`Usunąć pole „${field.label}” oraz reguły, które do niego odwołują?`)) return
  removeField(packageValue.value, path)
  selection.value = 'form'
  syncDefinitionOrder()
}

function syncDefinitionOrder(): void {
  const order = packageValue.value.presentation?.sections.flatMap((section) => section.field_paths) ?? []
  const map = new Map(packageValue.value.definition.fields.map((field) => [field.path, field]))
  packageValue.value.definition.fields = [
    ...order.map((path) => map.get(path)).filter((field): field is ClinicCrfField => Boolean(field)),
    ...packageValue.value.definition.fields.filter((field) => !order.includes(field.path)),
  ]
}

function insertClinicalMask(mask: ClinicCrfMask): void {
  try {
    const result = packageWithClinicalMask(packageValue.value, mask)
    current.value = result.package
    selection.value = result.sectionIds[0] ? `section:${result.sectionIds[0]}` : 'form'
    mode.value = 'build'
    sidebarOpen.value = true
    maskLibraryOpen.value = false
    notify(`Dodano maskę „${mask.title}” (${result.fieldPaths.length} pól).`)
  } catch (error) {
    notify((error as Error).message || 'Nie udało się dodać maski.')
  }
}

function addRule(): void {
  const field = packageValue.value.definition.fields[0]
  if (!field) return
  packageValue.value.definition.rules ??= []
  const existing = new Set(packageValue.value.definition.rules.map((rule) => rule.id))
  let index = packageValue.value.definition.rules.length + 1
  while (existing.has(`rule_${index}`)) index += 1
  packageValue.value.definition.rules.push({ id: `rule_${index}`, path: field.path, message: 'Warunek spójności nie został spełniony.', assert: { field: field.path, operator: 'present' } })
}

function updateRule(index: number, patch: Partial<ClinicCrfRule>): void {
  const rule = packageValue.value.definition.rules?.[index]
  if (rule) packageValue.value.definition.rules![index] = { ...rule, ...patch }
}

function updateRuleCondition(index: number, key: 'when' | 'assert', condition: ClinicCrfCondition | undefined): void {
  const rule = packageValue.value.definition.rules?.[index]
  if (!rule) return
  if (condition) rule[key] = condition
  else if (key === 'when') delete rule.when
}

function deleteRule(index: number): void { packageValue.value.definition.rules?.splice(index, 1) }

function downloadJson(value: unknown, filename: string): void {
  const blob = new Blob([prettyJson(value)], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

async function exportPackage(): Promise<void> {
  const result = validatePackage(packageValue.value)
  if (!result.value) { mode.value = 'quality'; notify('Popraw błędy przed eksportem pełnego pakietu.'); return }
  try {
    const output = await packageWithIntegrity(result.value)
    downloadJson(output, packageFileName(output))
    notify('Wyeksportowano pakiet z integralnością SHA-256.')
  } catch { notify('Ta przeglądarka nie udostępnia bezpiecznego SHA-256.') }
}

function exportCore(): void {
  if (!validateDefinitionV2(packageValue.value.definition).value) { mode.value = 'quality'; notify('Popraw błędy definicji przed eksportem.'); return }
  downloadJson(packageValue.value.definition, `${safeFilePart(packageValue.value.form.code)}-v${packageValue.value.form.version}.clinic-crf-core.json`)
  notify('Wyeksportowano rdzeń clinic-crf.v2.')
}

async function printAnnotated(): Promise<void> {
  annotated.value = true
  mode.value = 'preview'
  await nextTick()
  window.print()
  annotated.value = false
}

function clearDraft(): void {
  if (!window.confirm('Usunąć lokalny autosave? Bieżący formularz pozostanie otwarty do zamknięcia karty.')) return
  try { localStorage.removeItem(MAKER_DRAFT_KEY); draft.value = null; saveState.value = 'idle'; notify('Lokalny autosave został usunięty.') } catch { notify('Nie udało się usunąć autosave.') }
}
</script>

<template>
  <WelcomeScreen v-if="view === 'welcome'" :resume-label="draftLabel" :import-error="importError" @choose="chooseTemplate" @resume="resumeDraft" @import="importFile" />

  <div v-else class="app-shell">
    <a class="skip-link" href="#workspace-main">Przejdź do edytora</a>
    <header class="app-header no-print">
      <button class="brand brand-button" type="button" aria-label="Wróć do ekranu startowego" @click="goHome"><span class="brand-mark"><SquareCheckBig :size="19" :stroke-width="2.2" /></span><span><strong>StrefaBadań</strong><small>eCRF Maker</small></span></button>
      <div class="document-title"><span>{{ packageValue.form.name }}</span><code>{{ packageValue.form.code }} · v{{ packageValue.form.version }}</code></div>
      <nav class="mode-tabs" aria-label="Tryb pracy">
        <button :class="{ active: mode === 'build' }" type="button" @click="mode = 'build'"><FileCog :size="17" /> Buduj</button>
        <button :class="{ active: mode === 'preview' }" type="button" @click="mode = 'preview'"><FlaskConical :size="17" /> Testuj</button>
        <button :class="{ active: mode === 'quality' }" type="button" @click="mode = 'quality'"><FileCheck2 :size="17" /> Jakość <span :class="qualityErrors ? 'count-error' : 'count-ok'">{{ qualityErrors }}</span></button>
      </nav>
      <div class="header-actions">
        <div class="history-actions"><button class="icon-button" type="button" :disabled="!canUndo" title="Cofnij (Ctrl/Cmd+Z)" @click="undo"><Undo2 :size="18" /></button><button class="icon-button" type="button" :disabled="!canRedo" title="Ponów (Ctrl/Cmd+Shift+Z)" @click="redo"><Redo2 :size="18" /></button></div>
        <span :class="['save-status', saveState]"><Save :size="15" /><span>{{ saveState === 'saving' ? 'Zapisywanie…' : saveState === 'saved' ? 'Zapisano lokalnie' : saveState === 'error' ? 'Autosave niedostępny' : 'Tylko lokalnie' }}</span></span>
        <details class="export-menu"><summary class="button button-primary"><Download :size="17" /> Eksportuj <ChevronDown :size="15" /></summary><div class="menu-popover"><button type="button" @click="exportPackage"><BadgeCheck :size="18" /><span><strong>Pełny pakiet</strong><small>JSON + prezentacja + SHA-256</small></span></button><button type="button" @click="exportCore"><FileJson :size="18" /><span><strong>Rdzeń clinic-crf.v2</strong><small>Tylko interoperacyjna definicja</small></span></button><button type="button" @click="printAnnotated"><Printer :size="18" /><span><strong>Annotated CRF / PDF</strong><small>Ścieżki, typy i ograniczenia</small></span></button></div></details>
      </div>
    </header>

    <div class="privacy-strip no-print"><ShieldCheck :size="16" /><span><strong>Sandbox projektowy:</strong> nie wpisuj danych pacjentów ani uczestników. Maker nie wysyła treści do serwera i zapisuje lokalnie wyłącznie projekt: metadane, definicję oraz prezentację.</span><a href="https://clinic.strefaankiet.pl" target="_blank" rel="noopener noreferrer">Potrzebujesz kompletnego eCRF? <ExternalLink :size="14" /></a></div>

    <main id="workspace-main" :class="['workspace', `mode-${mode}`, { 'sidebar-closed': !sidebarOpen }]">
      <aside v-if="mode === 'build' && sidebarOpen" class="builder-sidebar no-print">
        <div class="sidebar-heading"><div><span class="eyebrow">Struktura</span><h2>Formularz</h2></div><button class="icon-button" type="button" title="Zwiń strukturę" @click="sidebarOpen = false"><PanelLeftClose :size="18" /></button></div>
        <button :class="['structure-root', { selected: selection === 'form' }]" type="button" @click="selectItem('form')"><Settings2 :size="18" /><span><strong>Metadane formularza</strong><small>{{ packageValue.form.code }} · v{{ packageValue.form.version }}</small></span></button>
        <button class="clinical-mask-launch" type="button" @click="maskLibraryOpen = true"><span><LibraryBig :size="19" /></span><div><strong>Dodaj maskę kliniczną</strong><small>12 gotowych bloków pytań</small></div><Sparkles :size="16" /></button>

        <div class="sections-list">
          <article v-for="(section, sectionIndex) in packageValue.presentation?.sections" :key="section.id" class="section-tree">
            <div :class="['section-row', { selected: selection === `section:${section.id}` }]">
              <button class="tree-select" type="button" @click="selectItem(`section:${section.id}`)"><LayoutList :size="17" /><span><strong>{{ section.title }}</strong><small>{{ section.field_paths.length }} pól · {{ section.id }}</small></span></button>
              <div class="row-actions"><button type="button" title="Sekcja wyżej" :disabled="sectionIndex === 0" @click="moveSection(sectionIndex, -1)"><ArrowUp :size="14" /></button><button type="button" title="Sekcja niżej" :disabled="sectionIndex === (packageValue.presentation?.sections.length ?? 0) - 1" @click="moveSection(sectionIndex, 1)"><ArrowDown :size="14" /></button><button type="button" title="Usuń sekcję" @click="deleteSection(section.id)"><Trash2 :size="14" /></button></div>
            </div>
            <div class="field-tree">
              <div v-for="(path, fieldIndex) in section.field_paths" :key="path" :class="['field-row', { selected: selection === `field:${path}` }]">
                <button class="drag-handle" type="button" tabindex="-1" aria-hidden="true"><GripVertical :size="14" /></button>
                <button class="tree-select" type="button" @click="selectItem(`field:${path}`)"><span><strong>{{ packageValue.definition.fields.find((field) => field.path === path)?.label ?? path }}</strong><small>{{ fieldTypeLabels[packageValue.definition.fields.find((field) => field.path === path)?.type ?? ''] }} · {{ path }}</small></span></button>
                <div class="row-actions field-actions"><button type="button" title="Pole wyżej" :disabled="fieldIndex === 0" @click="moveField(section.id, fieldIndex, -1)"><ArrowUp :size="13" /></button><button type="button" title="Pole niżej" :disabled="fieldIndex === section.field_paths.length - 1" @click="moveField(section.id, fieldIndex, 1)"><ArrowDown :size="13" /></button><button type="button" title="Duplikuj pole" @click="copyField(path)"><Copy :size="13" /></button><button type="button" title="Usuń pole" @click="deleteField(path)"><Trash2 :size="13" /></button></div>
              </div>
              <details class="add-field-menu inline"><summary><Plus :size="15" /> Dodaj pole</summary><div class="type-picker"><button v-for="type in FIELD_TYPES" :key="type" type="button" @click="addField(section.id, type)"><span>{{ fieldTypeLabels[type] }}</span><code>{{ type }}</code></button></div></details>
            </div>
          </article>
        </div>
        <button class="button button-outline add-section" type="button" @click="addSection"><FilePlus2 :size="16" /> Dodaj sekcję</button>
        <button :class="['structure-root rules-root', { selected: selection === 'rules' }]" type="button" @click="selectItem('rules')"><Braces :size="18" /><span><strong>Reguły cross-field</strong><small>{{ packageValue.definition.rules?.length ?? 0 }} reguł spójności</small></span></button>
        <div class="sidebar-footer"><button class="button button-quiet" type="button" @click="goHome"><ArrowLeft :size="16" /> Zmień szablon</button><button class="button button-quiet danger-text" type="button" @click="clearDraft"><Trash2 :size="15" /> Usuń autosave</button></div>
      </aside>

      <button v-if="mode === 'build' && !sidebarOpen" class="sidebar-reopen no-print" type="button" @click="sidebarOpen = true"><ClipboardList :size="18" /><span>Struktura</span></button>

      <section v-if="mode === 'build'" class="editor-canvas no-print">
        <div v-if="selection === 'form'" class="canvas-content">
          <header class="canvas-header"><div><span class="eyebrow">Ustawienia projektu</span><h1>Metadane formularza</h1><p>Identyfikują pakiet podczas importu i publikacji.</p></div><span class="step-badge"><Check :size="15" /> Autosave aktywny</span></header>
          <div class="editor-section large-section"><div class="form-grid two-columns"><label class="span-2"><span>Nazwa formularza</span><input :value="packageValue.form.name" maxlength="255" @input="updateForm('name', ($event.target as HTMLInputElement).value)" /></label><label><span>Kod formularza</span><input :value="packageValue.form.code" maxlength="60" spellcheck="false" @input="updateForm('code', ($event.target as HTMLInputElement).value.toUpperCase())" /><small>A–Z, cyfry, _ i -</small></label><label><span>Wersja</span><input type="number" min="1" step="1" :value="packageValue.form.version" @input="updateForm('version', Number(($event.target as HTMLInputElement).value))" /></label><label class="span-2"><span>Opis celu i zakresu</span><textarea rows="5" maxlength="4000" :value="packageValue.form.description ?? ''" placeholder="Dla kogo i w którym momencie badania przeznaczony jest formularz?" @input="updateForm('description', ($event.target as HTMLTextAreaElement).value || undefined)"></textarea></label><label><span>Domyślny język</span><input :value="packageValue.presentation?.default_locale ?? 'pl'" pattern="[a-z]{2}(-[A-Z]{2})?" @input="updateLocale(($event.target as HTMLInputElement).value)" /></label><div class="contract-card"><FileJson :size="20" /><span><strong>clinic-crf.v2</strong><small>Otwarty, wersjonowany kontrakt</small></span></div></div></div>
          <div class="next-card"><Sparkles :size="21" /><div><strong>Dobry początek</strong><p>Dodaj sekcje odpowiadające etapom wizyty, a potem pola. Panel jakości od razu wychwyci niespójności.</p></div><button class="button button-primary" type="button" @click="selectItem(packageValue.presentation?.sections[0] ? `section:${packageValue.presentation.sections[0].id}` : 'form')">Przejdź do sekcji <ArrowRight :size="16" /></button></div>
        </div>

        <div v-else-if="selectedSection" class="canvas-content">
          <header class="canvas-header"><div><span class="eyebrow">Sekcja {{ (packageValue.presentation?.sections.indexOf(selectedSection) ?? 0) + 1 }}</span><h1>{{ selectedSection.title }}</h1><p>Grupuj pola w logiczne części formularza.</p></div><span class="step-badge"><LayoutList :size="15" /> {{ selectedSection.field_paths.length }} pól</span></header>
          <div class="editor-section large-section"><div class="form-grid two-columns"><label class="span-2"><span>Tytuł sekcji</span><input :value="selectedSection.title" maxlength="500" @input="updateSection('title', ($event.target as HTMLInputElement).value)" /></label><label><span>Identyfikator sekcji</span><input :value="selectedSection.id" maxlength="80" spellcheck="false" @change="updateSection('id', ($event.target as HTMLInputElement).value)" /><small>lower_snake_case</small></label><label class="span-2"><span>Opis / instrukcja</span><textarea rows="4" maxlength="2000" :value="selectedSection.description ?? ''" @input="updateSection('description', ($event.target as HTMLTextAreaElement).value)"></textarea></label></div></div>
          <div class="section-field-summary"><h2>Pola w sekcji</h2><div v-if="selectedSection.field_paths.length" class="summary-list"><button v-for="(path, index) in selectedSection.field_paths" :key="path" type="button" @click="selectItem(`field:${path}`)"><span>{{ index + 1 }}</span><div><strong>{{ packageValue.definition.fields.find((field) => field.path === path)?.label }}</strong><code>{{ path }}</code></div><ArrowRight :size="17" /></button></div><div v-else class="empty-state"><FilePlus2 :size="28" /><strong>Ta sekcja nie ma jeszcze pól</strong><p>Dodaj pierwsze pole ze struktury po lewej.</p></div></div>
        </div>

        <div v-else-if="selectedField" class="canvas-content field-content">
          <header class="canvas-header sticky-canvas-header"><div><span class="eyebrow">Pole · {{ selectedField.type }}</span><h1>{{ selectedField.label }}</h1><p><code>{{ selectedField.path }}</code></p></div><div class="canvas-actions"><button class="button button-quiet" type="button" @click="copyField(selectedField.path)"><Copy :size="16" /> Duplikuj</button><button class="button button-quiet danger-text" type="button" @click="deleteField(selectedField.path)"><Trash2 :size="16" /> Usuń</button></div></header>
          <FieldEditor :field="selectedField" :presentation="selectedPresentation" :fields="packageValue.definition.fields" @update:field="updateField" @update:presentation="updateFieldPresentation" @rename="renameField" />
        </div>

        <div v-else-if="selection === 'rules'" class="canvas-content rules-content">
          <header class="canvas-header"><div><span class="eyebrow">Spójność danych</span><h1>Reguły cross-field</h1><p>Porównuj pola i komunikuj niezgodności. To część kontraktu, nie kod interfejsu.</p></div><button class="button button-primary" type="button" @click="addRule"><Plus :size="16" /> Dodaj regułę</button></header>
          <div v-if="packageValue.definition.rules?.length" class="rules-list"><article v-for="(rule, ruleIndex) in packageValue.definition.rules" :key="`${rule.id}-${ruleIndex}`" class="rule-card"><header><span><Braces :size="18" /><strong>Reguła {{ ruleIndex + 1 }}</strong></span><button class="icon-button danger-text" type="button" title="Usuń regułę" @click="deleteRule(ruleIndex)"><Trash2 :size="16" /></button></header><div class="form-grid two-columns"><label><span>Id reguły</span><input :value="rule.id" @input="updateRule(ruleIndex, { id: ($event.target as HTMLInputElement).value })" /></label><label><span>Przypisz błąd do pola</span><select :value="rule.path" @change="updateRule(ruleIndex, { path: ($event.target as HTMLSelectElement).value })"><option v-for="field in packageValue.definition.fields" :key="field.path" :value="field.path">{{ field.label }} · {{ field.path }}</option></select></label><label class="span-2"><span>Komunikat dla użytkownika</span><input :value="rule.message" maxlength="1000" @input="updateRule(ruleIndex, { message: ($event.target as HTMLInputElement).value })" /></label></div><ConditionEditor :model-value="rule.when" :fields="packageValue.definition.fields" title="Kiedy sprawdzać regułę (opcjonalnie)" @update:model-value="updateRuleCondition(ruleIndex, 'when', $event)" /><ConditionEditor :model-value="rule.assert" :fields="packageValue.definition.fields" title="Warunek, który musi być prawdziwy" @update:model-value="updateRuleCondition(ruleIndex, 'assert', $event)" /></article></div>
          <div v-else class="empty-state large-empty"><Braces :size="34" /><strong>Brak reguł cross-field</strong><p>Zakresy pojedynczych pól już działają. Dodaj regułę, gdy spójność zależy od innej odpowiedzi.</p><button class="button button-primary" type="button" @click="addRule"><Plus :size="16" /> Dodaj pierwszą regułę</button></div>
        </div>
      </section>

      <aside v-if="mode === 'build'" class="live-preview-column"><PreviewPane :package-value="packageValue" /></aside>
      <section v-if="mode === 'preview'" class="full-preview"><div class="full-view-heading no-print"><div><span class="eyebrow">Bezpieczny sandbox</span><h1>Przetestuj ścieżki formularza</h1><p>Wprowadzane wartości istnieją tylko w pamięci karty i nigdy nie trafiają do autosave.</p></div><button class="button button-outline" type="button" @click="printAnnotated"><Printer :size="16" /> Annotated CRF / PDF</button></div><PreviewPane :package-value="packageValue" :annotated="annotated" /></section>
      <section v-if="mode === 'quality'" class="quality-view"><QualityPanel :package-value="packageValue" /><div class="quality-guide"><span class="eyebrow">Przed publikacją</span><h2>Od szkicu do kontrolowanego eCRF</h2><p>Maker pomaga przygotować interoperacyjny pakiet. Prowadzenie badania wymaga dodatkowych kontroli: uprawnień, audit trail, queries, podpisów, blokad i zatwierdzonego procesu study-build.</p><ol><li><span>1</span><div><strong>Usuń błędy i przejrzyj sugestie</strong><p>Walidator korzysta z tego samego kontraktu co Reader.</p></div></li><li><span>2</span><div><strong>Wyeksportuj pakiet z SHA-256</strong><p>Integralność pozwala wykryć zmianę definicji.</p></div></li><li><span>3</span><div><strong>Zaimportuj jako draft do eCRF</strong><p>Publikacja pozostaje oddzielną, kontrolowaną decyzją.</p></div></li></ol><a class="cta-card" href="https://clinic.strefaankiet.pl" target="_blank" rel="noopener noreferrer"><span><small>StrefaBadań eCRF</small><strong>Gotowy na prowadzenie badania?</strong><em>Poznaj zarządzanie ośrodkami, rolami, audit trail i danymi uczestników.</em></span><ArrowRight :size="22" /></a><p class="cta-disclaimer"><CircleHelp :size="15" /> Narzędzie open source nie stanowi samo w sobie deklaracji zgodności regulacyjnej ani walidacji procesu użytkownika.</p></div></section>
    </main>

    <div class="mobile-tabs no-print"><button :class="{ active: mode === 'build' }" @click="mode = 'build'"><FileCog :size="19" /><span>Buduj</span></button><button :class="{ active: mode === 'preview' }" @click="mode = 'preview'"><FlaskConical :size="19" /><span>Testuj</span></button><button :class="{ active: mode === 'quality' }" @click="mode = 'quality'"><FileCheck2 :size="19" /><span>Jakość</span><b v-if="qualityErrors">{{ qualityErrors }}</b></button></div>
    <div class="toast no-print" :class="{ visible: toast }" role="status" aria-live="polite"><Check :size="17" /> {{ toast }}</div>
    <MaskLibrary v-if="maskLibraryOpen" @close="maskLibraryOpen = false" @insert="insertClinicalMask" />
  </div>
</template>
